export type IdentityProviderKind =
  | 'entra'
  | 'active-directory'
  | 'okta'
  | 'oidc'
  | 'saml'
  | 'scim';

export type ProviderStatus = 'connected' | 'not-connected' | 'syncing' | 'error';

export type FeatureBadge = 'Directory' | 'SSO' | 'Users' | 'Groups' | 'Provisioning';

export interface IdentityProviderRecord {
  id: string;
  kind: IdentityProviderKind;
  name: string;
  status: ProviderStatus;
  secondaryLabel?: string;
  userCount?: number;
  groupCount?: number;
  autoSync?: boolean;
  lastSync?: string;
  ssoEnabled?: boolean;
  features: FeatureBadge[];
  tenantId?: string;
  clientId?: string;
  organization?: string;
  configuredBy?: string;
  graphAccessVerified?: boolean;
  connectionId?: string;
}

export interface ProviderCatalogItem {
  kind: IdentityProviderKind;
  name: string;
  secondaryLabel?: string;
  description: string;
  features: FeatureBadge[];
  actionLabel: 'Connect' | 'Set Up' | 'Configure';
}

/** Example connected-provider shape for future UI. Not shown until mock/local state includes it. */
export const SAMPLE_ENTRA_PROVIDER: IdentityProviderRecord = {
  id: 'sample-entra',
  kind: 'entra',
  name: 'Microsoft Entra ID',
  status: 'connected',
  userCount: 1245,
  groupCount: 24,
  autoSync: true,
  lastSync: '5 minutes ago',
  ssoEnabled: true,
  features: ['Directory', 'Users', 'Groups', 'SSO'],
};

export const PROVIDER_CATALOG: ProviderCatalogItem[] = [
  {
    kind: 'entra',
    name: 'Microsoft Entra ID',
    description:
      'Connect Microsoft Entra ID to synchronize users and groups and configure enterprise SSO.',
    features: ['Directory', 'Users', 'Groups', 'SSO'],
    actionLabel: 'Connect',
  },
  {
    kind: 'active-directory',
    name: 'Active Directory',
    secondaryLabel: 'On-Premises',
    description: 'Connect your on-premises Active Directory using a secure directory connector.',
    features: ['Directory', 'Users', 'Groups'],
    actionLabel: 'Set Up',
  },
  {
    kind: 'okta',
    name: 'Okta',
    description: 'Connect Okta for user provisioning, group synchronization, and single sign-on.',
    features: ['Directory', 'Users', 'Groups', 'SSO'],
    actionLabel: 'Connect',
  },
  {
    kind: 'oidc',
    name: 'OpenID Connect',
    secondaryLabel: 'OIDC',
    description: 'Configure single sign-on with any OpenID Connect compatible identity provider.',
    features: ['SSO'],
    actionLabel: 'Configure',
  },
  {
    kind: 'saml',
    name: 'SAML 2.0',
    description: 'Configure enterprise single sign-on using a SAML 2.0 identity provider.',
    features: ['SSO'],
    actionLabel: 'Configure',
  },
  {
    kind: 'scim',
    name: 'SCIM 2.0',
    description: 'Configure standards-based user and group provisioning.',
    features: ['Users', 'Groups', 'Provisioning'],
    actionLabel: 'Configure',
  },
];

export const DEMO_SETUP_MESSAGE = 'This identity provider setup is not available yet.';

export function formatRelativeSync(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return 'Never';
  const minutes = Math.max(0, Math.round((Date.now() - then.getTime()) / 60000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
